/**
 * UNIT-SELECT.TSX
 * sélecteur/créateur d’unité indépendant.
 */

import { useState } from "react"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import unitStore from "@/features/risks/entities/unit/model/unit.store"
import { Unit, createEmptyUnit } from "@/features/risks/entities/unit/model/unit.types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowsRotate, faPlus, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import SearchBar from "@/components/search-bar"
import ListItem from "@/components/list-item"
import ContextMenu from "@/components/context-menu"
import { t } from "i18next"
import Tooltip from "@/components/tooltip"
import Space from "@/components/space"
import UnitSizeTooltip from "./UnitSizeTooltip"
import RadioBtn from "@/components/radio-btn"
import UnitAddModal from "./modals/UnitAddModal"
import riskStore from "@/features/risks/entities/risk/model/risk.store"

interface Props {
  selectMultiple?: boolean
  onSelect?: (unit: Unit | null) => void
  selectedUnit?: Unit | null | undefined
}

export default function UnitSelect({ selectMultiple = false, selectedUnit, onSelect }: Props) {
  const dispatch = useAppDispatch()

  const [displayList, setDisplayList] = useState(selectMultiple ? true : false)
  const [showAddModal, setShowAddModal] = useState(false)

  // --- Redux data
  const active = useAppSelector(unitStore.selectors.active)
  const units = useAppSelector(unitStore.selectors.list)
  const risks = useAppSelector(riskStore.selectors.list)

  function handleCreateUnit() {
    dispatch(unitStore.actions.setActive(createEmptyUnit()))
    setShowAddModal(true)
  }

  function handleCloseModal() {
    setShowAddModal(false)
  }

  function select(unit: Unit) {
    dispatch(unitStore.actions.setActive(unit))
    if (onSelect) {
      onSelect(unit)
      setDisplayList(false)
    }
  }

  function RenderList() {
    return (
      <>
        <SearchBar onSearch={(e) => dispatch(unitStore.actions.search({ value: e, columns: ["name"] }))} />
        <div className="h-[14px]" />
        <div className="modal-content-list">
          <div style={{ maxHeight: 182, overflowY: "auto" }}>
            <ListItem isSmall onClick={handleCreateUnit} isEditable className="items-center">
              <FontAwesomeIcon icon={faPlus} className="grey-t" />
              <div className="w-2" />
              <span className="grey-t font-semibold">{t("units_utils_create_new_node_short")}</span>
            </ListItem>
            {units.map((u) => (
              <ListItem key={u.id} isSmall isEditable isActive={u.id === selectedUnit?.id} onClick={() => select(u)}>
                {!onSelect && (
                  <RadioBtn
                    active={active?.id === u.id}
                    onClick={() => {
                      dispatch(unitStore.actions.setActive(u))
                    }}
                    className="grey-t"
                  />
                )}
                <div className="w-10" />
                {u.name}
                <Space />
                <Tooltip
                  text={`Nombre de risques: ${risks.filter((r) => r.UnitId === u.id).length}`}
                  position="left"
                  className="grey-t text-sm flex items-center">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  &nbsp;
                  {risks.filter((r) => r.UnitId === u.id).length}
                </Tooltip>
                <div className="w-10" />
                <div className="flex items-center">
                  <UnitSizeTooltip size={u.size} position="left" />
                </div>
              </ListItem>
            ))}
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="rel">
      {!selectMultiple && (
        <div className="grey-t mt-2 _hover" onClick={() => setDisplayList(true)}>
          {t("unit")}&nbsp;:&nbsp;
          {selectedUnit ? (
            <Tooltip text={t("Changer l'unité de travail associée")} position="top">
              <span className="mr-2">
                <b>{selectedUnit.name}</b>
              </span>
              <FontAwesomeIcon icon={faArrowsRotate} />
            </Tooltip>
          ) : (
            <span>
              <b className="red-t">Non défini</b>
            </span>
          )}
        </div>
      )}

      {displayList &&
        (selectMultiple ? (
          <div className="mt-3 w-full">{RenderList()}</div>
        ) : (
          <ContextMenu className="left-[1px] !top-[0px]" positionRight onClose={() => setDisplayList(false)}>
            {RenderList()}
          </ContextMenu>
        ))}

      {showAddModal && <UnitAddModal addRisks={false} onClose={handleCloseModal} />}
    </div>
  )
}
